import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Select from "react-select";
import { toast } from "react-toastify";
import { capitalizeWords } from "../../functions";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

function NoticeBoard() {
  const [noticeBoard, setNoticeBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];
  const [isEditing, setIsEditing] = useState(false);
  const [editNoticeId, setEditNoticeId] = useState(null);
  const [existingFileKey, setExistingFileKey] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    attachment: "",
    message: "",
    status: "",
    date: "",
    file: null,
  });

  const resetForm = () => {
    setFormData({
      title: "",
      attachment: "",
      message: "",
      status: "",
      date: "",
      file: null,
    });
    setIsEditing(false);
    setEditNoticeId(null);
    setExistingFileKey("");
    document.querySelector('input[type="file"]').value = "";
  };

  const handleEditClick = (notice) => {
    setFormData({
      title: notice.title,
      message: notice.message,
      date: notice.date,
      attachment: notice.attachment,
      status: notice.status,
      file: null,
    });
    setExistingFileKey(notice.attachment);
    setIsEditing(true);
    setEditNoticeId(notice.noticeId);

    const modal = new window.bootstrap.Modal(document.querySelector(".modal"));
    modal.show();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      file: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let fileKey = existingFileKey || "";

      if (formData.file) {
        const fileName = `${Date.now()}_${formData.file.name}`;
        const presignResponse = await axios.post(
          `${server}/notice_board_presigned`,
          { fileName: [fileName] },
        );

        const signedUrl =
          presignResponse.data["body-json"].body.uploadDetails[0].signedUrl;
        fileKey = presignResponse.data["body-json"].body.uploadDetails[0].key;

        await axios.put(signedUrl, formData.file, {
          headers: {
            "Content-Type": formData.file.type,
          },
        });
      }

      const payload = {
        noticeId: isEditing ? editNoticeId : `${Date.now()}`,
        date: todayDate,
        title: formData.title,
        attachment: fileKey,
        message: formData.message,
        status: formData.status,
      };

      const apiEndpoint = isEditing
        ? `${server}/update_notice_board_mongodb`
        : `${server}/noticeborad_post_mongodb`;

      await axios.post(apiEndpoint, payload, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
        },
      });

      toast.success(isEditing ? "Notice Updated" : "Notice Posted", {
        position: "top-right",
      });

      document.querySelector('[data-bs-dismiss="modal"]')?.click();

      fetchNotice();
      resetForm();
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const fetchNotice = async () => {
    try {
      const response = await axios.post(
        `${server}/notice_board_data_mongodb`,
        {},
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      );

      const data = response.data?.["body-json"]?.body?.data;

      if (data && Array.isArray(data)) {
        const sortedNotices = data.sort((a, b) => {
          const getIdNumber = (notice) => {
            const parts = notice.noticeId?.split("/");
            const numberPart = parts?.[parts.length - 1];
            return Number(numberPart);
          };

          return getIdNumber(b) - getIdNumber(a);
        });

        setNoticeBoard(sortedNotices);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching bus details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotice();
  }, []);

  // Filter
  const filteredRecords = noticeBoard.filter((scholarship) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(scholarship).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = async (noticeIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete notice "${noticeIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/notice_delete_mongodb`,
          { noticeId: noticeIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          toast.success("Notice Deleted", { position: "top-right" });

          setNoticeBoard((prev) =>
            prev.filter((notice) => notice.noticeId !== noticeIdToDelete),
          );
        } else {
          toast.error("Deletion failed. Please try again.");
        }
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const handleExportNoticeBoardExcel = () => {
    if (!noticeBoard || noticeBoard.length === 0) {
      toast.error("No notice data to export");
      return;
    }

    const sheetData = noticeBoard.map((notice) => ({
      "Notice ID": notice.noticeId || "",
      Title: notice.title || "",
      Message: notice.message || "",
      Status: capitalizeWords(notice.status || ""),
      Attachment: notice.attachment ? "Yes" : "No",
      Date: notice.date
        ? new Date(notice.date).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notice Board");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `NoticeBoard_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportNoticeBoardWithDocumentExcel = () => {
    if (!noticeBoard || noticeBoard.length === 0) {
      toast.error("No notice data to export");
      return;
    }

    const filteredNotices = noticeBoard.filter(
      (notice) => notice.attachment && notice.attachment.trim() !== "",
    );

    if (filteredNotices.length === 0) {
      toast.info("No notices with documents found");
      return;
    }

    const sheetData = filteredNotices.map((notice) => ({
      "Notice ID": notice.noticeId || "",
      Title: notice.title || "",
      Message: notice.message || "",
      Status: capitalizeWords(notice.status || ""),
      Attachment: "Yes",
      Date: notice.date
        ? new Date(notice.date).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Notice Board");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `NoticeBoard_WithDocument_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportNoticeBoardActiveExcel = () => {
    if (!noticeBoard || noticeBoard.length === 0) {
      toast.error("No notice data to export");
      return;
    }

    const filteredNotices = noticeBoard.filter(
      (notice) => (notice.status || "").toLowerCase() === "active",
    );

    if (filteredNotices.length === 0) {
      toast.info("No active notices found");
      return;
    }

    const sheetData = filteredNotices.map((notice, index) => ({
      "S.No": index + 1,
      "Notice ID": notice.noticeId || "",
      Title: notice.title || "",
      Message: notice.message || "",
      Status: capitalizeWords(notice.status || ""),
      Attachment: notice.attachment ? "Yes" : "No",
      Date: notice.date
        ? new Date(notice.date).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Active Notices");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `NoticeBoard_Active_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportNoticeBoardDeActiveExcel = () => {
    if (!noticeBoard || noticeBoard.length === 0) {
      toast.error("No notice data to export");
      return;
    }

    const filteredNotices = noticeBoard.filter(
      (notice) => (notice.status || "").toLowerCase() === "deactive",
    );

    if (filteredNotices.length === 0) {
      toast.info("No active notices found");
      return;
    }

    const sheetData = filteredNotices.map((notice, index) => ({
      "S.No": index + 1,
      "Notice ID": notice.noticeId || "",
      Title: notice.title || "",
      Message: notice.message || "",
      Status: capitalizeWords(notice.status || ""),
      Attachment: notice.attachment ? "Yes" : "No",
      Date: notice.date
        ? new Date(notice.date).toLocaleDateString("en-GB")
        : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "De-Active Notices");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `NoticeBoard_DeActive_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header d-flex justify-content-between">
                    <h5
                      className="modal-title fs-4"
                      id="exampleModalToggleLabel"
                    >
                      Add Notice
                    </h5>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div className="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked
                          readOnly
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <div className="row g-2 mb-2">
                      {/* Title */}
                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control text-dark"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          placeholder="Enter Title"
                          required
                        />
                      </div>

                      <div className="col-lg-6 col-12">
                        <label className="form-label mb-1 ms-3">
                          Attachment
                        </label>
                        <input
                          type="file"
                          className="form-control text-dark"
                          accept=".pdf,.doc,.docx,.png,.jpg"
                          onChange={handleFileChange}
                        />
                      </div>

                      <div className="col-lg-6 col-12">
                        <label class="form-label mb-1 ms-3">
                          Status <span className="text-danger">*</span>
                        </label>
                        <Select
                          options={[
                            { label: "Publish", value: "active" },
                            { label: "Save as Draft", value: "draft" },
                          ]}
                          value={
                            [
                              { label: "Publish", value: "active" },
                              { label: "Save as Draft", value: "draft" },
                            ].find((opt) => opt.value === formData.status) ||
                            null
                          }
                          onChange={(selected) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: selected?.value || "",
                            }))
                          }
                          placeholder="Select Status"
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label mb-1 ms-3">
                          Message <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control text-dark"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Enter Message"
                          rows="5"
                          required
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((noticeboard, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Notice ID</span> &mdash;{" "}
                              {noticeboard.noticeId}
                              <br />
                              <span class="text-dark">Title</span> &mdash;{" "}
                              {noticeboard.title
                                .toLowerCase()
                                .split(" ")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1),
                                )
                                .reduce((acc, word, index) => {
                                  const last = acc.length - 1;
                                  if (index % 5 === 0) {
                                    acc.push([word]);
                                  } else {
                                    acc[last].push(word);
                                  }
                                  return acc;
                                }, [])
                                .map((group, i) => (
                                  <span key={i}>
                                    {group.join(" ")}
                                    <br />
                                  </span>
                                ))}
                              <br />
                              <span class="text-dark">Message</span> &mdash;
                              {noticeboard.message
                                .toLowerCase()
                                .split(" ")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1),
                                )
                                .reduce((acc, word, index) => {
                                  const last = acc.length - 1;
                                  if (index % 5 === 0) {
                                    acc.push([word]);
                                  } else {
                                    acc[last].push(word);
                                  }
                                  return acc;
                                }, [])
                                .map((group, i) => (
                                  <span key={i}>
                                    {group.join(" ")}
                                    <br />
                                  </span>
                                ))}
                              <br />
                              <span class="text-dark">Status</span> &mdash;{" "}
                              {capitalizeWords(noticeboard.status)}
                              <br />
                              {noticeboard.attachment && (
                                <>
                                  <span class="text-dark">Attachment</span>{" "}
                                  &mdash;{" "}
                                  <a
                                    href={noticeboard.attachment}
                                    target="_blank"
                                  >
                                    View File
                                  </a>
                                  <br />
                                </>
                              )}
                              <span class="text-dark">Date</span> &mdash;{" "}
                              {noticeboard.date
                                ? new Date(noticeboard.date).toLocaleDateString(
                                    "en-GB",
                                  )
                                : ""}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span
                                  onClick={() => handleEditClick(noticeboard)}
                                  class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Update
                                </span>
                                <span
                                  onClick={() =>
                                    handleDelete(noticeboard.noticeId)
                                  }
                                  class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                >
                                  Delete
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="d-flex bg-white justify-content-between rounded-4">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                        <Link
                          data-bs-toggle="modal"
                          onClick={resetForm}
                          data-bs-target=".gd-example-modal-lg"
                          class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                        >
                          Add Notice
                        </Link>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Communicate</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Notice Board
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Notice Board
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              0.2 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  onClick={handleExportNoticeBoardExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Notice Report
                                </span>
                                <span
                                  onClick={
                                    handleExportNoticeBoardWithDocumentExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Notice With Document
                                </span>
                                <span
                                  onClick={handleExportNoticeBoardActiveExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Active Notice
                                </span>
                                <span
                                  onClick={handleExportNoticeBoardDeActiveExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  De-Active Notice
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default NoticeBoard;
